import {
  Directive,
  Optional,
  Host,
  Self,
  Component,
  ViewChild,
} from '@angular/core';
import {
  CellClickEvent,
  CellComponent,
  ColumnBase,
  ColumnComponent,
  GridComponent,
} from '@progress/kendo-angular-grid';
import {
  ContextMenuComponent,
  ContextMenuModule,
  ContextMenuSelectEvent,
} from '@progress/kendo-angular-menu';
import { GridRightClickMenuService } from './grid-right-click-menu.service';
import {
  CompositeFilterDescriptor,
  FilterDescriptor,
} from '@progress/kendo-data-query';

/**
 * Interface defining menu items for the grid right-click context menu.
 */
interface MenuItems {
  text?: string;
  childText?: string;
  children?: MenuItems[];
  svgIcon?: string;
  separator?: boolean;
  action?: (
    grid: GridComponent,
    cellEvent: CellClickEvent,
    menuEvent: ContextMenuSelectEvent
  ) => any;
}

/**
 * A component that provides a context menu for Kendo Grid cells on right-click.
 * 
 * This component renders a Kendo context menu with filtering options when a user
 * right-clicks on a grid cell. It supports common filtering operations like "Show Matching",
 * "Filter Out", and "Clear Column Filter".
 * 
 * @example
 * ```html
 * <!-- First add the directive to your grid -->
 * <kendo-grid></kendo-grid>
 * 
 * <!-- Then include the component in your template -->
 * <app-grid-context-menu></app-grid-context-menu>
 * ```
 */
@Component({
  selector: 'app-grid-context-menu',
  standalone: true,
  imports: [ContextMenuModule],
  template: `<kendo-contextmenu
    #gridmenu
    [kendoMenuHierarchyBinding]="menuItems"
    [textField]="['text', 'childText']"
    childrenField="children"
    svgIconField="svgIcon"
    separatorField="separator"
    (select)="onSelect($event)"
  ></kendo-contextmenu>`,
})
export class GridRightClickMenuComponent {
  /**
   * Reference to the Kendo context menu component.
   */
  @ViewChild('gridmenu') public gridContextMenu!: ContextMenuComponent;
  /**
   * The list of menu items to display in the context menu.
   * Each item contains text and an action function to execute when selected.
   */
  public menuItems: MenuItems[] = [
    {
      text: 'Show Matching',
      action: (grid, cellEvent, menuEvent) =>
        this.addFilter('eq', grid, cellEvent, menuEvent),
    },
    {
      text: 'Filter Out',
      action: (grid, cellEvent, menuEvent) =>
        this.addFilter('neq', grid, cellEvent, menuEvent),
    },
    {
      text: 'Clear Column Filter',
      action: (grid, cellEvent, menuEvent) =>
        this.clearFilter(grid, cellEvent, menuEvent),
    },
  ];

  /**
   * Creates an instance of GridRightClickMenuComponent.
   * @param service The service that coordinates between the grid, directive and menu.
   */
  constructor(private service: GridRightClickMenuService) {
    service.menu = this;
  }

  /**
   * Handles the selection event when a menu item is clicked.
   * Executes the associated action function of the selected menu item.
   * @param event The context menu selection event.
   */
  public onSelect(event: ContextMenuSelectEvent): void {
    console.log('GridRightClickMenuComponent: onSelect');
    let item = event.item.data as MenuItems;
    if (item.action) {
      let grid = this.service.grid;
      let cellEvent = this.service.event;
      if (grid) {
        console.log('GridRightClickMenuComponent: Calling action');
        item.action(grid, cellEvent!, event);
      } else {
        console.log('GridRightClickMenuComponent: No grid');
      }
    }
  }

  /**
   * Adds a filter to the grid based on the selected cell value.
   * Supports different filter operators and handles special case for date filters.
   * @param operator The filter operator to use (e.g., 'eq', 'neq').
   * @param grid The Kendo grid component instance.
   * @param cellEvent The cell click event containing the cell data.
   * @param menuEvent The context menu selection event.
   */
  addFilter(
    operator: string,
    grid: GridComponent,
    cellEvent: CellClickEvent,
    menuEvent: ContextMenuSelectEvent
  ) {
    let column = cellEvent.column as ColumnComponent;

    let field = column.field;
    let value = cellEvent.dataItem[field];
    let filter = column.filter;

    let filters =
      grid.filter ||
      ({
        filters: [],
        logic: 'and',
      } as CompositeFilterDescriptor);

    switch (filter) {
      case 'text':
        filters.filters.push({
          field: field,
          operator: operator,
          value: value,
        });
        break;
      case 'numeric':
        filters.filters.push({
          field: field,
          operator: operator,
          value: value,
        });
        break;
      case 'boolean':
        filters.filters.push({
          field: field,
          operator: operator,
          value: value,
        });
        break;
      case 'date':
        if (value != null) {
          //Split date into 2 datetimes, 12:00 AM and 11:59:59 PM
          let dateValue = new Date((<Date>value).getTime());
          dateValue.setHours(0, 0, 0, 0);
          let date1 = new Date(dateValue.getTime());
          dateValue.setDate(dateValue.getDate() + 1);
          dateValue.setMilliseconds(dateValue.getMilliseconds() - 1);
          let date2 = new Date(dateValue.getTime());

          let operator1 = '';
          let operator2 = '';
          let logic: 'and' | 'or' | undefined = undefined;

          if (operator == 'eq') {
            //Include datetimes between date1 and date2
            operator1 = 'gte';
            operator2 = 'lte';
            logic = 'and';
          } else {
            //Exclude datetimes between date1 and date2
            operator1 = 'lt';
            operator2 = 'gt';
            logic = 'or';
          }

          filters.filters.push({
            logic: logic,
            filters: [
              {
                field: field,
                operator: operator1,
                value: date1,
              },
              {
                field: field,
                operator: operator2,
                value: date2,
              },
            ],
          });
        } else {
          //Null filter
          filters.filters.push({
            field: field,
            operator: operator,
            value: value,
          });
        }
        break;
    }

    grid.filter = filters;
    //grid.filterChange.emit(filters);
    this.service.filterService?.filter(filters);
  }

  /**
   * Clears all filters for the selected column from the grid.
   * Removes both simple and composite filters associated with the column.
   * @param grid The Kendo grid component instance.
   * @param cellEvent The cell click event containing the cell data.
   * @param menuEvent The context menu selection event.
   */
  clearFilter(
    grid: GridComponent,
    cellEvent: CellClickEvent,
    menuEvent: ContextMenuSelectEvent
  ) {
    //Find all filters applied to this field and remove from the array
    let column = cellEvent.column as ColumnComponent;

    let field = column.field;
    let value = cellEvent.dataItem[field];
    let filter = column.filter;

    let filters =
      grid.filter ||
      ({
        filters: [],
        logic: 'and',
      } as CompositeFilterDescriptor);

    for (let i = 0; i < filters.filters.length; i++) {
      let filter = filters.filters[i] as FilterDescriptor;
      if (filter.field !== undefined) {
        if (filter.field == field) {
          filters.filters.splice(i, 1);
          i -= 1;
        }
      } else {
        let filterList = filters.filters[i] as CompositeFilterDescriptor;
        if (
          filterList.filters !== undefined &&
          filterList.filters.length > 0 &&
          (filterList.filters[0] as FilterDescriptor).field == field
        ) {
          filters.filters.splice(i, 1);
          i -= 1;
        }
      }
    }
    //grid.filter = filters;
    //grid.filterChange.emit(filters);
    this.service.filterService?.filter(filters);
  }
}
